import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/cn';
import { BLOCK_CAT_ORDER, BLOCK_TONES } from './block-tones';
import { Icon } from './icon';

export type BlockCat =
  | 'blockDeep' | 'blockMeeting' | 'blockAdmin' | 'blockBuffer' | 'blockLunch' | 'blockPersonal'
  | 'blockCreative' | 'blockLearning' | 'blockSocial' | 'blockFitness' | 'blockTravel' | 'blockRoutine';

export function BlockContextMenu({
  x, y, current, onPick, onDelete, onClose,
}: {
  x: number;
  y: number;
  current: BlockCat;
  onPick: (cat: BlockCat) => void;
  onDelete?: () => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [onClose]);

  const isDark = document.body.classList.contains('dark');

  const MENU_W = 200;
  const MENU_H = 34 + BLOCK_CAT_ORDER.length * 28 + (onDelete ? 38 : 0) + 10;
  const left = Math.min(x, window.innerWidth - MENU_W - 8);
  const top = Math.min(y, window.innerHeight - MENU_H - 8);

  return createPortal(
    <div
      ref={ref}
      className="fixed z-[1000] card shadow-modal py-1.5 animate-fade-in"
      style={{ left, top, width: MENU_W }}
    >
      <div className="px-3 pt-1 pb-1.5 text-[11px] tracking-[-0.1px] text-fg dark:text-dark-fg font-medium">
        Farbe
      </div>
      {BLOCK_CAT_ORDER.map((k) => {
        const t = BLOCK_TONES[k];
        const active = k === current;
        const swatch = isDark ? t.swatchDark : t.swatch;
        return (
          <button
            key={k}
            onClick={() => onPick(k as BlockCat)}
            className={cn(
              'w-full flex items-center gap-2.5 px-3 py-[5px] text-[12.5px] tracking-[-0.1px] text-fg dark:text-dark-fg hover:bg-paperAlt dark:hover:bg-dark-paperAlt transition',
              active && 'font-semibold',
            )}
          >
            <span
              className="w-[12px] h-[12px] rounded-full border border-black/10 dark:border-white/20 shrink-0"
              style={{ background: swatch }}
            />
            <span className="flex-1 text-left">{t.label}</span>
            {active && (
              <svg width="10" height="10" viewBox="0 0 16 16" aria-hidden className="opacity-70">
                <path d="M3 8.5L6.5 12L13 4.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </button>
        );
      })}
      {onDelete && (
        <>
          <div className="my-1 h-px bg-line dark:bg-dark-line" />
          <button
            onClick={onDelete}
            className="w-full flex items-center gap-2 px-3 py-[6px] text-[12.5px] tracking-[-0.1px] text-[#c33] dark:text-[#f59] hover:bg-paperAlt dark:hover:bg-dark-paperAlt transition"
          >
            <Icon name="trash" size={11} />
            <span>Block löschen</span>
          </button>
        </>
      )}
    </div>,
    document.body,
  );
}
